import React, { useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CustomFieldConfig, ConditionalMessageConfig } from './CustomFieldsBuilder';
import { evaluateCondition } from '@/utils/evaluate-condition.util';

interface DynamicFieldProps {
  field: CustomFieldConfig;
  form: UseFormReturn<any>;
  disabled?: boolean;
}

export const DynamicField: React.FC<DynamicFieldProps> = ({
  field,
  form,
  disabled = false
}) => {
  // Use nested path for customFields
  const fieldName = `customFields.${field.fieldKey}`;

  const renderField = () => {
    switch (field.fieldType) {
      case 'text':
        return (
          <FormField
            control={form.control}
            name={fieldName}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-status-danger ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    {...formField}
                    placeholder={field.placeholder}
                    disabled={disabled}
                  />
                </FormControl>
                {field.description && (
                  <FormDescription>{field.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'number':
        return (
          <FormField
            control={form.control}
            name={fieldName}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-status-danger ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    {...formField}
                    type="number"
                    placeholder={field.placeholder}
                    disabled={disabled}
                    onChange={(e) => {
                      const value = e.target.value;
                      formField.onChange(value ? Number(value) : '');
                    }}
                  />
                </FormControl>
                {field.description && (
                  <FormDescription>{field.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'date':
        return (
          <FormField
            control={form.control}
            name={fieldName}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-status-danger ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    {...formField}
                    type="date"
                    placeholder={field.placeholder}
                    disabled={disabled}
                  />
                </FormControl>
                {field.description && (
                  <FormDescription>{field.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'textarea':
        return (
          <FormField
            control={form.control}
            name={fieldName}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-status-danger ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...formField}
                    placeholder={field.placeholder}
                    disabled={disabled}
                    rows={3}
                  />
                </FormControl>
                {field.description && (
                  <FormDescription>{field.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'select':
        return (
          <FormField
            control={form.control}
            name={fieldName}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-status-danger ml-1">*</span>}
                </FormLabel>
                <Select
                  onValueChange={formField.onChange}
                  defaultValue={formField.value}
                  disabled={disabled}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={field.placeholder || 'Selecione uma opção'} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {field.description && (
                  <FormDescription>{field.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'checkbox':
        return (
          <FormField
            control={form.control}
            name={fieldName}
            render={({ field: formField }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={formField.value}
                    onCheckedChange={formField.onChange}
                    disabled={disabled}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    {field.label}
                    {field.required && <span className="text-status-danger ml-1">*</span>}
                  </FormLabel>
                  {field.description && (
                    <FormDescription>{field.description}</FormDescription>
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      default:
        return null;
    }
  };

  return <div className="space-y-4">{renderField()}</div>;
};

// Variant of DynamicField that uses an explicit fieldPath instead of computing it from fieldKey
interface GroupItemFieldProps {
  field: CustomFieldConfig;
  fieldPath: string;
  form: UseFormReturn<any>;
  disabled?: boolean;
}

const GroupItemField: React.FC<GroupItemFieldProps> = ({ field, fieldPath, form, disabled = false }) => {
  const renderField = () => {
    switch (field.fieldType) {
      case 'text':
        return (
          <FormField
            control={form.control}
            name={fieldPath}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-status-danger ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input {...formField} placeholder={field.placeholder} disabled={disabled} />
                </FormControl>
                {field.description && <FormDescription>{field.description}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'number':
        return (
          <FormField
            control={form.control}
            name={fieldPath}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-status-danger ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    {...formField}
                    type="number"
                    placeholder={field.placeholder}
                    disabled={disabled}
                    onChange={(e) => {
                      const value = e.target.value;
                      formField.onChange(value ? Number(value) : '');
                    }}
                  />
                </FormControl>
                {field.description && <FormDescription>{field.description}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'date':
        return (
          <FormField
            control={form.control}
            name={fieldPath}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-status-danger ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input {...formField} type="date" placeholder={field.placeholder} disabled={disabled} />
                </FormControl>
                {field.description && <FormDescription>{field.description}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'textarea':
        return (
          <FormField
            control={form.control}
            name={fieldPath}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-status-danger ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Textarea {...formField} placeholder={field.placeholder} disabled={disabled} rows={3} />
                </FormControl>
                {field.description && <FormDescription>{field.description}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'select':
        return (
          <FormField
            control={form.control}
            name={fieldPath}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {field.label}
                  {field.required && <span className="text-status-danger ml-1">*</span>}
                </FormLabel>
                <Select onValueChange={formField.onChange} defaultValue={formField.value} disabled={disabled}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={field.placeholder || 'Selecione uma opção'} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {field.description && <FormDescription>{field.description}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'checkbox':
        return (
          <FormField
            control={form.control}
            name={fieldPath}
            render={({ field: formField }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox checked={formField.value} onCheckedChange={formField.onChange} disabled={disabled} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    {field.label}
                    {field.required && <span className="text-status-danger ml-1">*</span>}
                  </FormLabel>
                  {field.description && <FormDescription>{field.description}</FormDescription>}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      default:
        return null;
    }
  };

  return <div className="space-y-4">{renderField()}</div>;
};

// Sub-field inside a repeat group section with showWhen evaluated against section values
// merged with top-level form values so conditions can reference both scopes.
interface ConditionalSubFieldProps {
  field: CustomFieldConfig;
  form: UseFormReturn<any>;
  disabled?: boolean;
  groupKey: string;
  sectionIndex: number;
  topLevelValues?: Record<string, any>;
}

const ConditionalSubField: React.FC<ConditionalSubFieldProps> = ({
  field, form, disabled, groupKey, sectionIndex, topLevelValues,
}) => {
  const sectionValues = useWatch({ control: form.control, name: `customFields.${groupKey}[${sectionIndex}]` }) ?? {};
  // Merge top-level values (fallback) with section values (priority) so showWhen
  // conditions can reference both fields within the section and top-level fields.
  const mergedValues = topLevelValues ? { ...topLevelValues, ...sectionValues } : sectionValues;
  const isVisible = !field.showWhen || evaluateCondition(field.showWhen, mergedValues);

  useEffect(() => {
    if (!isVisible) {
      form.unregister(`customFields.${groupKey}[${sectionIndex}].${field.fieldKey}`);
    }
  }, [isVisible, field.fieldKey, form, groupKey, sectionIndex]);

  if (!isVisible) return null;

  const fieldPath = `customFields.${groupKey}[${sectionIndex}].${field.fieldKey}`;
  return <GroupItemField field={field} fieldPath={fieldPath} form={form} disabled={disabled} />;
};

// Section component for a single repeat group instance
interface RepeatGroupSectionProps {
  sectionIndex: number;
  groupKey: string;
  groupLabel: string;
  groupFields: CustomFieldConfig[];
  form: UseFormReturn<any>;
  disabled?: boolean;
  topLevelValues?: Record<string, any>;
}

const RepeatGroupSection: React.FC<RepeatGroupSectionProps> = ({
  sectionIndex, groupKey, groupLabel, groupFields, form, disabled, topLevelValues,
}) => {
  const sortedFields = [...groupFields].sort((a, b) => a.order - b.order);

  return (
    <div className="border border-border rounded-ds-card p-4 space-y-4 bg-muted">
      <h4 className="font-medium text-sm text-foreground">
        {groupLabel} {sectionIndex + 1}
      </h4>
      {sortedFields.map((subField) => (
        <ConditionalSubField
          key={subField.fieldKey}
          field={subField}
          form={form}
          disabled={disabled}
          groupKey={groupKey}
          sectionIndex={sectionIndex}
          topLevelValues={topLevelValues}
        />
      ))}
    </div>
  );
};

// Banner component for conditional messages shown below a field
interface ConditionalMessageBannerProps {
  conditionalMessage: ConditionalMessageConfig;
  formValues: Record<string, any>;
}

const ConditionalMessageBanner: React.FC<ConditionalMessageBannerProps> = ({
  conditionalMessage,
  formValues,
}) => {
  const isVisible = evaluateCondition(conditionalMessage.showWhen, formValues);

  if (!isVisible) return null;

  const styleMap = {
    info: 'bg-primary/10 text-primary border border-primary/30',
    warning: 'bg-brand-attention/10 text-status-attention border border-brand-attention/30',
    error: 'bg-brand-danger/10 text-status-danger border border-brand-danger/30',
  };

  const iconMap = {
    info: 'ℹ️',
    warning: '⚠️',
    error: '❌',
  };

  return (
    <div className={`rounded-md p-3 text-sm flex items-start gap-2 ${styleMap[conditionalMessage.type]}`}>
      <span aria-hidden="true">{iconMap[conditionalMessage.type]}</span>
      <span>{conditionalMessage.message}</span>
    </div>
  );
};

// Wrapper component that handles conditional visibility and unregisters hidden fields
interface ConditionalFieldProps {
  field: CustomFieldConfig;
  form: UseFormReturn<any>;
  disabled?: boolean;
  formValues: Record<string, any>;
}

const ConditionalField: React.FC<ConditionalFieldProps> = ({ field, form, disabled, formValues }) => {
  const isVisible = !field.showWhen || evaluateCondition(field.showWhen, formValues);
  // useWatch creates a proper subscription so this component re-renders when the field value changes.
  // This is needed for repeat group count to update when the user types a number.
  const fieldValue = useWatch({ control: form.control, name: `customFields.${field.fieldKey}` });

  useEffect(() => {
    if (!isVisible) {
      form.unregister(`customFields.${field.fieldKey}`);
      if (field.repeatGroup) {
        form.unregister(`customFields.${field.repeatGroup.groupKey}`);
      }
    }
  }, [isVisible, field.fieldKey, field.repeatGroup, form]);

  // Sync repeat group array length with the count field value.
  // - Trims when count decreases so submission only sends active items.
  // - Fills with empty objects when count increases or array is missing, ensuring the
  //   array always has the expected number of items (fixes conditional fields that hide
  //   all sub-fields, which would otherwise leave the array empty).
  // Guard against !isVisible: when the field is hidden, fieldValue becomes undefined due to
  // unregister — not because the user cleared the count — so syncing would wrongly reset the array.
  useEffect(() => {
    if (!field.repeatGroup || !isVisible) return;
    const count = Math.max(0, Math.min(Number(fieldValue) || 0, field.repeatGroup.maxRepetitions ?? 20));
    const groupKey = field.repeatGroup.groupKey;
    const currentArray = form.getValues(`customFields.${groupKey}`) as any[] | undefined;
    if (Array.isArray(currentArray) && currentArray.length > count) {
      form.setValue(`customFields.${groupKey}`, currentArray.slice(0, count));
    } else if (count > 0 && (!Array.isArray(currentArray) || currentArray.length < count)) {
      // Ensure array has exactly `count` items, preserving existing data
      const newArray = Array.from({ length: count }, (_, i) =>
        (Array.isArray(currentArray) && currentArray[i]) ? currentArray[i] : {}
      );
      form.setValue(`customFields.${groupKey}`, newArray);
    }
  }, [fieldValue, field.repeatGroup, form, isVisible]);

  if (!isVisible) {
    return null;
  }

  const fieldElement = <DynamicField field={field} form={form} disabled={disabled} />;

  const messageBanner = field.conditionalMessage ? (
    <ConditionalMessageBanner
      conditionalMessage={field.conditionalMessage}
      formValues={formValues}
    />
  ) : null;

  if (field.repeatGroup) {
    const count = Math.max(0, Math.min(Number(fieldValue) || 0, field.repeatGroup.maxRepetitions ?? 20));

    return (
      <div className="space-y-4">
        {fieldElement}
        {messageBanner}
        {Array.from({ length: count }, (_, i) => (
          <RepeatGroupSection
            key={`${field.repeatGroup!.groupKey}-${i}`}
            sectionIndex={i}
            groupKey={field.repeatGroup!.groupKey}
            groupLabel={field.repeatGroup!.groupLabel}
            groupFields={field.repeatGroup!.fields}
            form={form}
            disabled={disabled}
            topLevelValues={formValues}
          />
        ))}
      </div>
    );
  }

  if (messageBanner) {
    return (
      <div className="space-y-2">
        {fieldElement}
        {messageBanner}
      </div>
    );
  }

  return fieldElement;
};

// Component for rendering a list of dynamic fields
interface DynamicFieldListProps {
  fields: CustomFieldConfig[];
  form: UseFormReturn<any>;
  disabled?: boolean;
  formValues?: Record<string, any>;
}

export const DynamicFieldList: React.FC<DynamicFieldListProps> = ({
  fields,
  form,
  disabled = false,
  formValues = {},
}) => {
  // Sort fields by order
  const sortedFields = [...fields].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-4">
      {sortedFields.map((field) => (
        <ConditionalField
          key={field.fieldKey}
          field={field}
          form={form}
          disabled={disabled}
          formValues={formValues}
        />
      ))}
    </div>
  );
};

// Helper function to get initial values for custom fields
export const getCustomFieldsInitialValues = (fields: CustomFieldConfig[]) => {
  const initialValues: Record<string, any> = {};

  fields.forEach((field) => {
    if (field.defaultValue !== undefined) {
      initialValues[field.fieldKey] = field.defaultValue;
    } else {
      switch (field.fieldType) {
        case 'checkbox':
          initialValues[field.fieldKey] = false;
          break;
        case 'number':
          initialValues[field.fieldKey] = '';
          break;
        case 'select':
          initialValues[field.fieldKey] = '';
          break;
        default:
          initialValues[field.fieldKey] = '';
      }
    }
  });

  return initialValues;
};